/**
 * Lightweight streetview marker renderer for the house composer.
 * Handles blue sphere markers + hover/click — no photo panel, no panorama session.
 * The consumer wires onNodeClick to open its own UI (e.g. a modal).
 */
import * as THREE from "three";
import type { StreetViewManifest } from "../types.ts";

const MARKER_RADIUS = 0.035;
const DRAG_THRESHOLD_PX = 5;
const MARKER_COLOR       = 0x4a9eff;
const MARKER_COLOR_HOVER  = 0x80c4ff;
const MARKER_COLOR_ACTIVE = 0xffffff;

export interface SvNodeInfo {
  id: number;
  position: THREE.Vector3;
}

export class StreetViewMarkers {
  readonly markersGroup = new THREE.Group();
  readonly nodes = new Map<number, SvNodeInfo>();
  sortedNodeIds: number[] = [];

  private readonly _geo = new THREE.SphereGeometry(MARKER_RADIUS, 8, 6);
  private readonly _mat        = new THREE.MeshBasicMaterial({ color: MARKER_COLOR });
  private readonly _matHover   = new THREE.MeshBasicMaterial({ color: MARKER_COLOR_HOVER });
  private readonly _matActive  = new THREE.MeshBasicMaterial({ color: MARKER_COLOR_ACTIVE });

  private _hoveredId: number | null = null;
  private _activeId:  number | null = null;

  private _downX = 0;
  private _downY = 0;

  private readonly _raycaster = new THREE.Raycaster();

  onNodeClick: ((nodeId: number) => void) | null = null;

  private readonly _onMove:        (e: MouseEvent) => void;
  private readonly _onClick:       (e: MouseEvent) => void;
  private readonly _onPointerDown: (e: PointerEvent) => void;

  constructor(
    private readonly _camera: THREE.PerspectiveCamera,
    private readonly _renderer: THREE.WebGLRenderer,
    parentGroup: THREE.Group,
  ) {
    parentGroup.add(this.markersGroup);
    this._onMove        = (e) => this._handleMove(e);
    this._onClick       = (e) => this._handleClick(e);
    this._onPointerDown = (e) => { this._downX = e.clientX; this._downY = e.clientY; };
    _renderer.domElement.addEventListener("pointerdown", this._onPointerDown);
    _renderer.domElement.addEventListener("mousemove",   this._onMove);
    _renderer.domElement.addEventListener("click",       this._onClick);
  }

  /** Load nodes from manifest. modelOffset is the world-space origin of the RTABMap scan. */
  loadManifest(manifest: StreetViewManifest, modelOffset: THREE.Vector3): void {
    this.markersGroup.clear();
    this.nodes.clear();

    // local_transform: 12-element 3×4 row-major, rotation only (used for camera dirs).
    // Node positions: SLAM uses Z-up; THREE.js uses Y-up.
    //   three.x = slam.x + offset.x
    //   three.y = slam.z + offset.y   (SLAM z → THREE y)
    //   three.z = −slam.y + offset.z  (SLAM y → −THREE z)
    for (const node of manifest.nodes) {
      const p = node.position;
      const pos = new THREE.Vector3(
        p.x + modelOffset.x,
        p.z + modelOffset.y,
        -p.y + modelOffset.z,
      );
      this.nodes.set(node.id, { id: node.id, position: pos });

      const mesh = new THREE.Mesh(this._geo, this._mat);
      mesh.position.copy(pos);
      mesh.userData["nodeId"] = node.id;
      this.markersGroup.add(mesh);
    }
    this.sortedNodeIds = [...this.nodes.keys()].sort((a, b) => a - b);
  }

  setVisible(visible: boolean): void { this.markersGroup.visible = visible; }

  setActiveNode(nodeId: number | null): void {
    this._activeId = nodeId;
    this._refreshColors();
  }

  private _ndc(e: MouseEvent): THREE.Vector2 {
    const rect = this._renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width)  * 2 - 1,
      -((e.clientY - rect.top)  / rect.height) * 2 + 1,
    );
  }

  private _hit(e: MouseEvent): number | null {
    this._raycaster.setFromCamera(this._ndc(e), this._camera);
    const hits = this._raycaster.intersectObjects(this.markersGroup.children);
    return hits.length ? (hits[0].object.userData["nodeId"] as number) : null;
  }

  private _handleMove(e: MouseEvent): void {
    const id = this._hit(e);
    if (id !== this._hoveredId) {
      this._hoveredId = id;
      this._refreshColors();
      this._renderer.domElement.style.cursor = id !== null ? "pointer" : "";
    }
  }

  private _handleClick(e: MouseEvent): void {
    if (Math.hypot(e.clientX - this._downX, e.clientY - this._downY) >= DRAG_THRESHOLD_PX) return;
    const id = this._hit(e);
    if (id !== null) {
      this._activeId = id;
      this._refreshColors();
      this.onNodeClick?.(id);
    }
  }

  private _refreshColors(): void {
    for (const child of this.markersGroup.children) {
      const mesh = child as THREE.Mesh;
      const id = mesh.userData["nodeId"] as number;
      mesh.material = id === this._activeId
        ? this._matActive
        : id === this._hoveredId
          ? this._matHover
          : this._mat;
    }
  }

  dispose(): void {
    this.markersGroup.removeFromParent();
    this.markersGroup.clear();
    this._geo.dispose();
    this._mat.dispose();
    this._matHover.dispose();
    this._matActive.dispose();
    this._renderer.domElement.removeEventListener("pointerdown", this._onPointerDown);
    this._renderer.domElement.removeEventListener("mousemove",   this._onMove);
    this._renderer.domElement.removeEventListener("click",       this._onClick);
  }
}
